import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';

// Custom Light Mode Icon Component
const LightModeIcon = ({ sx = {} }) => (
  <Box sx={{ width: 20, height: 20, position: 'relative', ...sx }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.9" />
      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="12" y1="1" x2="12" y2="3" opacity="0.8" />
        <line x1="12" y1="21" x2="12" y2="23" opacity="0.8" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" opacity="0.7" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" opacity="0.7" />
        <line x1="1" y1="12" x2="3" y2="12" opacity="0.8" />
        <line x1="21" y1="12" x2="23" y2="12" opacity="0.8" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" opacity="0.7" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" opacity="0.7" />
      </g>
    </svg>
  </Box>
);

// Custom Dark Mode Icon Component
const DarkModeIcon = ({ sx = {} }) => (
  <Box sx={{ width: 20, height: 20, position: 'relative', ...sx }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        fill="currentColor"
        opacity="0.9"
      />
      <circle cx="19" cy="5" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="8" r="0.5" fill="currentColor" opacity="0.6" />
      <circle cx="18" cy="9" r="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  </Box>
);

// System / Auto Icon Component (monitor with gear)
const SystemModeIcon = ({ sx = {} }) => (
  <Box sx={{ width: 20, height: 20, position: 'relative', ...sx }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      {/* Monitor */}
      <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none" />
      {/* Screen split: left light, right dark */}
      <rect x="3.5" y="4.5" width="8.5" height="11" fill="currentColor" opacity="0.15" />
      <rect x="12" y="4.5" width="8.5" height="11" fill="currentColor" opacity="0.55" />
      {/* Stand */}
      <line x1="12" y1="17" x2="12" y2="20" stroke="currentColor" strokeWidth="1.8" />
      <line x1="8" y1="20" x2="16" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  </Box>
);

const PREF_LABEL = { light: 'Light', dark: 'Dark', system: 'System' };
const PREF_NEXT  = { system: 'light', light: 'dark', dark: 'system' };
const PREF_ICON  = { light: LightModeIcon, dark: DarkModeIcon, system: SystemModeIcon };

const ThemeToggle = ({ sx = {} }) => {
  const { themeMode, themePreference, toggleTheme } = useThemeMode();

  const tooltipText = `${PREF_LABEL[themePreference]} mode — click for ${PREF_LABEL[PREF_NEXT[themePreference]]}`;
  const Icon = PREF_ICON[themePreference];

  return (
    <Tooltip
      title={tooltipText}
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: themeMode === 'light' ? 'rgba(30, 41, 59, 0.9)' : 'rgba(15, 23, 42, 0.95)',
            color: '#ffffff',
            fontSize: '0.8rem',
            fontWeight: 500,
            borderRadius: 1,
            backdropFilter: 'blur(20px)',
            border: themeMode === 'light'
              ? '1px solid rgba(99, 102, 241, 0.3)'
              : '1px solid rgba(251, 191, 36, 0.3)',
            maxWidth: 200,
            textAlign: 'center',
          }
        }
      }}
    >
      <IconButton
        onClick={toggleTheme}
        sx={{
          position: 'relative',
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'transparent',
          border: 'none',
          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'scale(1.1)',
            background: themeMode === 'light'
              ? 'rgba(99, 102, 241, 0.1)'
              : 'rgba(251, 191, 36, 0.1)',
          },
          ...sx,
        }}
        aria-label={`Theme: ${PREF_LABEL[themePreference]}`}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: themeMode === 'light'
              ? '#6366f1'
              : '#FBBF24',
            transition: 'all 0.3s ease',
          }}
        >
          <Icon />
        </Box>
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;
